import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Copy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { copyTextToClipboard } from '@/lib/clipboard'
import { getErrorMessage } from '@/lib/api'
import resourcePlannerApi from '@/services/resource-planner'
import type { StoreOrderDetail } from '@/types'

type PlanningRequirementJsonDialogProps = {
  order: StoreOrderDetail
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive'
  buttonClassName?: string
}

const toPkValue = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || !/^\d+$/.test(trimmed)) {
      return null
    }

    const parsed = Number(trimmed)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
  }

  return null
}

export function PlanningRequirementJsonDialog({
  order,
  buttonVariant = 'outline',
  buttonClassName,
}: PlanningRequirementJsonDialogProps) {
  const [open, setOpen] = useState(false)
  const normalizedOrderId = useMemo(() => toPkValue(order.raw_id), [order.raw_id])
  const contractQuery = useQuery({
    queryKey: ['planner-order-contract-preview', normalizedOrderId ?? null, order.reference_id],
    queryFn: () => resourcePlannerApi.buildContractForOrder(
      normalizedOrderId != null
        ? { order_id: normalizedOrderId }
        : { reference_id: order.reference_id },
    ),
    enabled: open && (normalizedOrderId != null || Boolean(order.reference_id)),
    staleTime: 60_000,
  })

  const payload = contractQuery.data ?? {
    contract_version: 'v4',
    resource_requirements: [],
  }
  const jsonText = useMemo(() => JSON.stringify(payload, null, 2), [payload])

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(jsonText)
      toast.success('Requirement JSON copied')
    } catch {
      toast.error('Failed to copy requirement JSON')
    }
  }

  return (
    <>
      <Button type="button" variant={buttonVariant} className={buttonClassName} onClick={() => setOpen(true)}>
        Requirement JSON
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Planning input contract</DialogTitle>
            <DialogDescription>
              JSON preview of the resource-requirement contract derived from the matched planner links on this booking.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {contractQuery.isFetching ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Building planning contract preview...
              </div>
            ) : null}
            {contractQuery.isError ? (
              <div className="rounded-lg border border-amber-300/70 bg-amber-50/80 p-3 text-sm text-amber-950">
                {getErrorMessage(contractQuery.error)}
              </div>
            ) : null}
            {(payload.resource_requirements ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 p-3 text-sm text-muted-foreground">
                No planner resource links matched this booking yet, so the contract currently contains an empty requirement list.
              </div>
            ) : null}

            <div className="rounded-lg border border-sky-200/80 bg-sky-50/80 p-3 text-sm text-sky-950">
              <p>
                This preview mixes concrete planning inputs with resource-definition metadata so the planning engine contract stays inspectable.
              </p>
              <p className="mt-2">
                Resolved values usually appear in <span className="font-medium">resource_requirements</span>, <span className="font-medium">resource_capabilities</span>, and <span className="font-medium">resource_dependencies</span> when planner link configuration provides concrete counts, constraints, costs, or dependency settings.
              </p>
              <p className="mt-2 text-sky-900/80">
                Schema-only fields remain as descriptive metadata from the planner resource definition when no concrete override has been configured yet.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy JSON
              </Button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 text-slate-50">
              <pre className="max-h-[60vh] overflow-auto p-4 text-xs leading-6">{jsonText}</pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PlanningRequirementJsonDialog